import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const COACH_IMG = 'https://customer-assets.emergentagent.com/job_669fbeff-f080-4dde-bc05-7b6a5c8193e8/artifacts/iahj2lkj_IMG_5808%202.jpg';

const SECTIONS = [
  { title: 'Coaching Mindset', text: 'Football is about solving problems. Every game, every practice, every film session is an opportunity to find the real issue, build the correction, and execute the plan. Braxton approaches football with a problem-solving mindset rooted in preparation, discipline, and accountability.' },
  { title: 'Offensive Philosophy', text: 'Build an offense that is multiple, efficient, and hard to defend. Attack defensive structure, create explosive opportunities, win the red zone, convert on third down, and protect the football. The system should fit the players, not force players into a system.' },
  { title: 'Program Building Philosophy', text: 'Programs are built on standards, not slogans. Culture is what you enforce, not what you hang on the wall. Structure creates freedom. Accountability builds trust. Development creates depth. Operations create consistency.' },
  { title: 'Why Film Audits', text: 'Film is the most honest teacher in football. It shows what actually happened, not what was supposed to happen. Film audits give coaches, players, and parents clear answers they can use to improve. Every film audit is designed to deliver specific corrections and usable next steps.' },
  { title: 'Who Weldon Helps', text: 'Coaches who need another trained set of eyes. Players who need honest development feedback. Parents who need clarity about their athlete. Programs that need structure, identity, and direction. Anyone serious about getting better through the film.' },
];

const BIO_TEXT = [
  'Weldon Braxton is a football coach, program builder, and culture setter whose career has been defined by leadership, standards, and the ability to build winning operations from the ground up.',
  'His coaching story began early. As a sophomore in high school, Braxton took charge of the defense for his school\'s powder puff football team. The result was three straight undefeated seasons. When Braxton was given the chance to lead, he brought structure, accountability, and winning with him.',
  'That builder mentality became even more evident in college, when he founded the Golden Eagles football program at Arkansas Tech University from scratch. In one of the most competitive flag football environments in Arkansas during the late 2000s, he built the program into one of the top teams in the state.',
  'At the junior college level, Braxton helped build a non scholarship program into a national power. He led the program to its first bowl game in school history and its first conference championship in school history. During that same stretch, he was recognized nationally as a member of the AFCA 35 Under 35.',
  'His leadership style is demanding, honest, and results driven. He has never confused being liked with leading well. He has embraced the hard and often uncomfortable work required to make organizations stronger, more efficient, and more successful.',
  'Today, Braxton remains hungry, driven, and fully committed to climbing to the highest levels of the profession. His story is still being written, but the foundation is already clear: he is a builder, a leader, and a coach whose best chapters are still ahead.',
];

export default function AboutWeldon() {
  return (
    <div className="bg-gray-950 text-white pt-20">
      <section className="py-16 px-6 border-b border-gray-800">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-blue-400 font-semibold tracking-widest uppercase text-sm mb-4">About</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">About Weldon Braxton</h1>
          <p className="text-lg text-gray-400">Football coach, offensive strategist, program builder, and film audit consultant.</p>
        </div>
      </section>

      {/* Bio */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-3 gap-10">
            <div className="md:col-span-1">
              <img src={COACH_IMG} alt="Coach Weldon Braxton" className="rounded-2xl w-full shadow-xl" />
            </div>
            <div className="md:col-span-2 space-y-5">
              {BIO_TEXT.map((p, i) => (
                <p key={i} className="text-gray-400 leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16 px-6 bg-gray-900 border-y border-gray-800">
        <div className="container mx-auto max-w-5xl space-y-10">
          {SECTIONS.map((s) => (
            <div key={s.title} className="border-l-2 border-blue-600 pl-6">
              <h3 className="text-xl font-bold mb-3">{s.title}</h3>
              <p className="text-gray-400 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold mb-10">Work With Weldon</h2>
          <Link to="/contact" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-full transition-all inline-flex items-center">
            Complete the Intake Form <ChevronRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
